import {showMessage} from 'exsportia-components'
import {Formik, FormikConfig} from 'formik'
import {FC, useCallback, useMemo, useRef, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {Outlet, useNavigate, useParams} from 'react-router-dom'

import {BookingQueries} from '../../../api/booking'
import {
  classBookingAdapter,
  payByTokenClassBookingAdapter,
  PayByTokenClassBookingDraft,
  removeFormFromBookingData,
} from '../../../api/booking/helpers'
import {Equipment} from '../../../api/booking/types'
import {Class} from '../../../api/class/types'
import {Session, SessionSelectSession} from '../../../api/class-session/types'
import {BookingFormData} from '../../../api/forms/types'
import {getEntityDataByIdFromAppearanceSettings} from '../../../api/helpers/data'
import {PromoError} from '../../../api/promo-codes/types'
import {ServiceTypesEnum} from '../../../api/service/types'
import {CreatedBookingsModal} from '../../../components/created-bookings-modal/created-bookings-modal'
import {handlePromoPayError} from '../../../components/promo-code-field/handle-promo-pay-error'
import {handleAddonInvalidError} from '../../../helpers/addon-error-message'
import {isContactBlockedError, showBlockedMessage} from '../../../helpers/blocked-member'
import {usePassNotValidHandler} from '../../../helpers/use-pass-not-valid-handler'
import {useLocale} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {defaultUserValues, IntegratedSignUpFormType} from '../../auth/forms/integrated-sign-up-form'
import {chargeErrorMessage} from '../../payments/helpers/charge-error-message'
import {showChargeRetryHintIfDegraded} from '../../payments/helpers/charge-retry-hint'
import {useHandleDraftEntityStatusChange} from '../../payments/hooks/handle-draft-entity-status-change.hook'
import {usePaymentProvider} from '../../payments/hooks/payment-provider-form.provider'
import {useBookingLoader} from '../hooks'
import {BookingService} from '../index'
import {RedirectionComponent} from './components/redirection-component'
import {useOpenBookingValidationModal} from './hooks/open-booking-validation-modal.hook'
import {BookingStateParticipant, SelectedSession} from './types'

export type CreateClassBookingFormType = IntegratedSignUpFormType & {
  venueId?: string
  entity: 'class'
  date: string | null
  promoCode?: string | null
  promoRejection?: PromoError | null
  class: Class | null
  bookingType: 'class'
  areaId: string | null
  specialRequest: string
  classId: string | null
  maxParticipants: number
  minParticipants: number
  session: Session | null
  timestamp: number | null
  sessionId: string | null
  coverEquipment?: boolean
  hasSpecialRequests: boolean
  participantsNames?: boolean
  classEquipments: Equipment[]
  sportType?: ServiceTypesEnum
  paymentMethodId: string | null
  userId: string | null | undefined
  splitPayment?: boolean
  closestSessionDate: string | null
  duration?: number | string | null
  participantsNamesWhenFull?: boolean
  participants: BookingStateParticipant[]
  instructors: SessionSelectSession['instructors'] | null
  paymentType?: 'oneTimePayment' | 'payNow' | 'payOnLocation'
  useForParticipants?: boolean
  // Multi-session booking
  selectedSessions: SelectedSession[]
  sharedParticipantsConfig: boolean
  perSessionParticipants: Record<string, BookingStateParticipant[]>
  perSessionUseForParticipants: Record<string, boolean>
  handledSlots: string[]
  currentSessionIndex: number
  formData?: BookingFormData
  formsData?: Record<string, BookingFormData>
}

type Params = {
  venueId: string
  classId: string
}

export const CreateClassBookingForm: FC = () => {
  // const luxon = useLuxon()
  const {venueId, classId} = useParams<Params>()
  const [entityId, setEntityId] = useState<string | null>(null)
  const navigate = useNavigate()
  const {l} = useLocale()
  const {openModal, closeAllModals} = useModalActions()
  const {isLoading, setIsLoading} = useBookingLoader()
  const {openBookingValidationModal} = useOpenBookingValidationModal()
  const handlePassNotValid = usePassNotValidHandler()
  const {openPaymentForm} = usePaymentProvider()
  const {mutate: createDraftBooking} = useMutation(BookingService.mutations.createClassBookingDraftMutation({}))
  const {data: priceDataObjects} = useQuery(BookingQueries.calculateBookingPriceQuery({enabled: false}))

  const classData =
    venueId && classId ? (getEntityDataByIdFromAppearanceSettings(venueId, classId) as Class | null) : null

  const {mutate: createClassBooking} = useMutation(BookingService.mutations.createClassBookingMutation({}))
  const {mutate: createPayByTokenClassBooking} = useMutation(
    BookingService.mutations.createClassBookingPayByTokenMutation({}),
  )
  // Captured at submit time so every post-create / post-payment path can list the created
  // bookings. For the token flows the booking uids equal objectsToSave[i].bookingId. WBA-4103.
  const multiSessionRef = useRef<{sessions: SelectedSession[]; bookingIds: (string | undefined)[]} | null>(null)
  const openCreatedBookingsModal = useCallback(
    (sessions: SelectedSession[], bookingIds: (string | undefined)[]) => {
      closeAllModals()
      // Leave the booking/pay flow first so dismissing the modal lands the user on their bookings
      // list — not back on the pay button, where they could pay a second time. WBA-4103.
      navigate(`/${venueId}/bookings`)
      openModal({
        title: l('bookings'),
        footerConfig: {disabled: true},
        component: (
          <CreatedBookingsModal
            bookings={sessions.map((session, index) => ({
              bookingId: bookingIds[index],
              title: session.className,
              subtitle: `${session.date} · ${session.time}`,
            }))}
            onSelect={(bookingId) => {
              closeAllModals()
              navigate(`/${venueId}/bookings/${bookingId}`)
            }}
          />
        ),
      })
    },
    [openModal, closeAllModals, navigate, venueId, l],
  )
  // The oneTimePayment (Exsportia Pay) flow never reaches the mutation onSuccess below — it
  // redirects from this hook after the payment clears. Open the modal here instead.
  const handleMultiSessionPaymentSuccess = useCallback(() => {
    const captured = multiSessionRef.current
    if (!captured || captured.sessions.length <= 1) {
      return false
    }
    openCreatedBookingsModal(captured.sessions, captured.bookingIds)
    return true
  }, [openCreatedBookingsModal])
  const {setStartRefetch} = useHandleDraftEntityStatusChange({
    entity: 'booking',
    entityId,
    isLoading,
    setIsLoading,
    onBookingPaymentSuccess: handleMultiSessionPaymentSuccess,
  })

  const initialValues = useMemo<CreateClassBookingFormType>(
    () => ({
      date: null,
      areaId: null,
      entity: 'class',
      venueId,
      session: null,
      classId: classId || null,
      timestamp: null,
      sessionId: null,
      class: classData,
      participants: [],
      instructors: null,
      minParticipants: 1,
      specialRequest: '',
      classEquipments: [],
      bookingType: 'class',
      paymentMethodId: null,
      closestSessionDate: classData?.closestSessionTimestamp || null,
      maxParticipants: Infinity,
      hasSpecialRequests: false,
      selectedSessions: [],
      sharedParticipantsConfig: true,
      perSessionParticipants: {},
      perSessionUseForParticipants: {},
      handledSlots: [],
      currentSessionIndex: 0,
      formData: undefined,
      ...defaultUserValues,
    }),
    [venueId, classData, classId],
  )

  const handleSubmit = useCallback<FormikConfig<CreateClassBookingFormType>['onSubmit']>(
    (values, formikHelpers) => {
      const {setFieldValue} = formikHelpers
      if (!priceDataObjects) {
        return
      }
      const specialRequestElement =
        typeof document !== 'undefined'
          ? (document.getElementById('specialRequest') as HTMLInputElement | HTMLTextAreaElement | null)
          : null
      const specialRequestFromDom = specialRequestElement?.value ?? ''
      const valuesWithSpecialRequest: CreateClassBookingFormType = values.hasSpecialRequests
        ? {...values, specialRequest: specialRequestFromDom}
        : {...values, specialRequest: ''}
      const isPayNow = !!(values.paymentMethodId && values.paymentType !== 'payOnLocation')
      const createBookingFunc = isPayNow ? createPayByTokenClassBooking : createClassBooking
      const dataForAdapter = {
        bookingState: valuesWithSpecialRequest,
        priceDataObjects,
      }
      let adaptedData = null
      let dataForDraft: PayByTokenClassBookingDraft | null = null
      if (values.paymentType !== 'payOnLocation') {
        adaptedData = payByTokenClassBookingAdapter(dataForAdapter)
        dataForDraft = adaptedData
      } else {
        adaptedData = classBookingAdapter(dataForAdapter)
      }
      // payByToken nests the drafts under .data; classBooking exposes them at the top level.
      const objectsToSave: {bookingId?: string}[] =
        (adaptedData as any)?.objectsToSave || (adaptedData as any)?.data?.objectsToSave || [] // eslint-disable-line
      multiSessionRef.current =
        values.selectedSessions.length > 1
          ? {sessions: values.selectedSessions, bookingIds: objectsToSave.map((o) => o.bookingId)}
          : null
      if (values.paymentType === 'oneTimePayment') {
        if (dataForDraft === null) {
          return
        }
        const dataForDraftToUse = dataForDraft as PayByTokenClassBookingDraft
        const submitDraft = (data: any, isRetry = false) => {
          createDraftBooking(data, {
            onSuccess: () => {
              setStartRefetch(true)
              setEntityId(dataForDraftToUse.bookingId || null)
              openPaymentForm &&
                openPaymentForm({
                  totalPrice: dataForDraftToUse.data.totalPrice,
                  bookingId: dataForDraftToUse.bookingId,
                  entity: 'booking',
                  startProcessCallback: () => setIsLoading(true),
                })
            },
            onError: (error: any) => {
              if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                submitDraft(removeFormFromBookingData(data), true)
                return
              }
              if (handleAddonInvalidError(error)) {
                return
              }
              if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                return
              }
              if (handlePassNotValid(error, {participants: values.participants, setFieldValue, perSessionParticipants: values.perSessionParticipants, perSessionUseForParticipants: values.perSessionUseForParticipants})) {
                return
              }
              if (isContactBlockedError(error)) {
                return showBlockedMessage()
              }
              if (error?.response?.status === 400 && error?.response?.data?.type === 'TIME_SLOT_NOT_AVAILABLE') {
                setFieldValue('session', null)
                setFieldValue('sessionId', null)
                setFieldValue('timestamp', null)
                openBookingValidationModal({type: 'class'})
              }
            },
          })
        }
        submitDraft(adaptedData)
      } else {
        setEntityId(null)
        setIsLoading(true)
        const submitBooking = (data: any, isRetry = false) => {
          createBookingFunc(data, {
            onSuccess: (responseData: any) => {
              closeAllModals()
              setIsLoading(false)
              // payOnLocation returns the array of created bookings; pay-by-token returns
              // {bookingId, bookingData, uid}. Normalise to the created-bookings list. WBA-4103.
              const createdBookings: any[] = Array.isArray(responseData) // eslint-disable-line
                ? responseData
                : Array.isArray(responseData?.bookingData)
                ? responseData.bookingData
                : [responseData]
              // Multi-session: list every created booking in a modal so the user can open any of
              // them, instead of dropping them into just the first booking's profile. WBA-4103.
              if (values.selectedSessions.length > 1 && createdBookings.length > 1) {
                openCreatedBookingsModal(
                  values.selectedSessions,
                  createdBookings.map((b) => b?.uid),
                )
                return
              }
              const firstBookingId = createdBookings[0]?.uid || responseData?.bookingId
              navigate(`/${venueId}/bookings/${firstBookingId}`)
            },
            onError: (error: any) => {
              if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                submitBooking(removeFormFromBookingData(data), true)
                return
              }
              setIsLoading(false)
              if (handleAddonInvalidError(error)) {
                return
              }
              if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                return
              }
              if (handlePassNotValid(error, {participants: values.participants, setFieldValue, perSessionParticipants: values.perSessionParticipants, perSessionUseForParticipants: values.perSessionUseForParticipants})) {
                return
              }
              if (isContactBlockedError(error)) {
                return showBlockedMessage()
              }
              if (error.response?.status === 409) {
                setFieldValue('session', null)
                setFieldValue('sessionId', null)
                setFieldValue('timestamp', null)
                return openBookingValidationModal({type: 'class'})
              }
              if (isPayNow) {
                const chargeError = chargeErrorMessage(l, error)
                if (chargeError) {
                  showMessage(chargeError, 'error')
                } else {
                  showChargeRetryHintIfDegraded(venueId)
                }
              }
            },
          })
        }
        submitBooking(adaptedData)
      }
    },
    [
      venueId,
      priceDataObjects,
      createClassBooking,
      createPayByTokenClassBooking,
      setIsLoading,
      navigate,
      closeAllModals,
      openCreatedBookingsModal,
      handlePassNotValid,
      l,
    ],
  )

  return (
    <Formik
      validateOnChange={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <RedirectionComponent>
        <Outlet />
      </RedirectionComponent>
    </Formik>
  )
}
